import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { TimeRangeSelector } from '@/components/analytics/TimeRangeSelector'
import { Loader2, BarChart3, Globe, Users, TrendingUp } from 'lucide-react'
import { useSiteAnalytics, type TimeRangeOption } from '@/hooks/analytics/useSiteAnalytics'
import { SiteAnalyticsOverview } from '@/components/analytics/SiteAnalyticsOverview'
import { SiteAnalyticsCharts } from '@/components/analytics/SiteAnalyticsCharts'
import { Helmet } from 'react-helmet'

const SiteAnalytics = () => {
  const [timeRange, setTimeRange] = useState<TimeRangeOption>('7days')
  const [activeTab, setActiveTab] = useState('overview')
  
  const { data: siteData, isLoading } = useSiteAnalytics(timeRange)
  
  return (
    <div className="space-y-6">
      <Helmet>
        <title>Site Analytics | Trotting the World</title>
      </Helmet>
      
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold">Site Analytics</h1>
          <p className="text-muted-foreground">
            Privacy-first analytics for your entire website.
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="pages">Top Pages</TabsTrigger>
          <TabsTrigger value="sources">Traffic Sources</TabsTrigger>
          <TabsTrigger value="audience">Audience</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview">
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="ml-2 text-muted-foreground">Loading site analytics...</p>
            </div>
          ) : siteData ? (
            <div className="space-y-6">
              <SiteAnalyticsOverview
                totalVisits={siteData.totalVisits}
                uniqueVisitors={siteData.uniqueVisitors}
                avgSessionDuration={siteData.avgSessionDuration}
                bounceRate={siteData.bounceRate}
                realTimeVisitors={siteData.realTimeVisitors}
                deviceBreakdown={siteData.deviceBreakdown}
              />
              <SiteAnalyticsCharts
                dailyVisits={siteData.dailyVisits}
                trafficSources={siteData.trafficSources}
                topPages={siteData.topPages}
                browserBreakdown={siteData.browserBreakdown}
              />
            </div>
          ) : (
            <div className="text-center py-20">
              <Globe className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No analytics data available</p>
            </div>
          )}
        </TabsContent>
        
        <TabsContent value="pages">
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : siteData ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5" />
                  Top Pages Performance
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {siteData.topPages.map((page, index) => (
                    <div key={page.page_path} className="flex items-center justify-between border-b pb-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-muted-foreground w-6">
                            #{index + 1}
                          </span>
                          <div>
                            <div className="font-medium">
                              {page.page_title || 'Untitled Page'}
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {page.page_path}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-6 text-sm">
                        <div className="text-center">
                          <div className="font-semibold">{page.page_views}</div>
                          <div className="text-muted-foreground">Views</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold">{page.unique_visitors}</div>
                          <div className="text-muted-foreground">Visitors</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold">
                            {Math.round(page.avg_time_on_page / 60)}m
                          </div>
                          <div className="text-muted-foreground">Avg Time</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : null}
        </TabsContent>
        
        <TabsContent value="sources">
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : siteData ? (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Traffic Sources Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {siteData.trafficSources.map((source, index) => (
                    <div key={`${source.source_name}-${source.source_type}`} className="flex items-center justify-between border-b pb-3">
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-medium text-muted-foreground w-6">
                          #{index + 1}
                        </span>
                        <div>
                          <div className="font-medium capitalize">{source.source_name}</div>
                          <div className="text-sm text-muted-foreground capitalize">
                            {source.source_type}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-6 text-sm">
                        <div className="text-center">
                          <div className="font-semibold">{source.visit_count}</div>
                          <div className="text-muted-foreground">Visits</div>
                        </div>
                        <div className="text-center">
                          <div className="font-semibold">{source.unique_visitors}</div>
                          <div className="text-muted-foreground">Visitors</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ) : null}
        </TabsContent>
        
        <TabsContent value="audience">
          {isLoading ? (
            <div className="flex justify-center items-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : siteData ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Device Types
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {siteData.deviceBreakdown.map((device) => (
                      <div key={device.device_type} className="flex items-center justify-between">
                        <span className="capitalize font-medium">{device.device_type}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">
                            {device.count} visits
                          </span>
                          <span className="font-semibold">
                            {device.percentage.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Browser Usage</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {siteData.browserBreakdown.map((browser) => (
                      <div key={browser.browser_type} className="flex items-center justify-between">
                        <span className="capitalize font-medium">{browser.browser_type}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">
                            {browser.count} visits
                          </span>
                          <span className="font-semibold">
                            {browser.percentage.toFixed(1)}%
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="md:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="h-5 w-5" />
                    Geographic Distribution
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">Country-level data only (GDPR compliant)</p>
                </CardHeader>
                <CardContent>
                  {siteData.countryBreakdown && siteData.countryBreakdown.length > 0 ? (
                    <div className="grid gap-3 md:grid-cols-2">
                      {siteData.countryBreakdown.map((country) => (
                        <div key={country.country} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                          <span className="font-medium">{country.country}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">
                              {country.count} visits
                            </span>
                            <span className="font-semibold">
                              {country.percentage.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No geographic data available yet</p>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : null}
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default SiteAnalytics
